import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { Spinner } from "@/components/ui/Spinner";
import { getWodyOrderCrossings } from "../api";

const EVENT_LABELS: Record<string, string> = {
  przeciecie_cieku: "Przecięcie cieku",
  wejscie_dzialka_wp: "Wejście na działkę WP",
  zblizenie_wal: "Zbliżenie do wału",
  teren_zalewowy: "Teren zalewowy",
  reczne: "Dodane ręcznie",
};

/** Wykaz przekroczeń i zbliżeń w ZAMÓWIENIU „Wody Polskie" — zamrożony snapshot
 *  zakresu (rodzaje, cieki, działki, organy WP, współrzędne). To samo widzą obie
 *  strony: nasz zespół na stronie zamówienia i podwykonawca w swoim panelu;
 *  podwykonawca poza snapshotem i załącznikami nie widzi nic z projektu. */
export function WodyOrderCrossingsPanel({ orderId }: { orderId: string }) {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["pp", "wody-order-crossings", orderId],
    queryFn: () => getWodyOrderCrossings(orderId),
    retry: false,
  });

  if (isLoading) return <div className="flex justify-center py-6"><Spinner size="sm" /></div>;
  if (isError) {
    // 404 = powiązanie zwolnione (zamówienie wycofane/anulowane) — panel znika.
    // Inny błąd NIE może po cichu chować zakresu prac podwykonawcy.
    const status = (error as AxiosError | undefined)?.response?.status;
    if (status === 404) return null;
    return (
      <div className="bg-white p-5 text-sm text-red-600 dark:bg-gray-800">
        Nie udało się pobrać wykazu przekroczeń — odśwież stronę.
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="bg-white p-5 dark:bg-gray-800">
      <h3 className="mb-3 font-mono text-[0.7rem] font-bold uppercase tracking-widest text-gray-500 dark:text-gray-400">
        Wykaz przekroczeń i zbliżeń — Wody Polskie ({data.length})
      </h3>
      {data.length === 0 ? (
        <p className="text-sm text-gray-500">Zamówienie nie ma pozycji wykazu (zakres opisany w treści/załącznikach).</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-200 text-left font-mono uppercase tracking-wider text-[10px] text-gray-500 dark:border-gray-700">
                <th className="py-1.5 pr-2">#</th>
                <th className="py-1.5 pr-2">Rodzaj</th>
                <th className="py-1.5 pr-2">Zdarzenie</th>
                <th className="py-1.5 pr-2">Ciek / obiekt</th>
                <th className="py-1.5 pr-2">Działka</th>
                <th className="py-1.5 pr-2">Teren zalewowy</th>
                <th className="py-1.5 pr-2">Organ WP</th>
                <th className="py-1.5 pr-2">Współrzędne (WGS84)</th>
                <th className="py-1.5">Notatka</th>
              </tr>
            </thead>
            <tbody>
              {data.map((c, i) => (
                <tr key={i} className="border-b border-gray-100 dark:border-gray-700/60">
                  <td className="py-1.5 pr-2 font-mono">{i + 1}</td>
                  <td className="py-1.5 pr-2">{c.kind_label || c.kind}</td>
                  <td className="py-1.5 pr-2">{EVENT_LABELS[c.event_type] ?? c.event_type}</td>
                  <td className="py-1.5 pr-2">{c.feature_name || "—"}</td>
                  <td className="py-1.5 pr-2 font-mono">{c.parcel || "—"}</td>
                  <td className="py-1.5 pr-2">{c.in_oszp_zone ? "TAK" : "nie"}</td>
                  <td className="py-1.5 pr-2">{c.wp_office?.nadzor || c.wp_office?.zarzad || c.wp_office?.rzgw || "—"}</td>
                  <td className="py-1.5 pr-2 font-mono">
                    {c.lat != null && c.lng != null ? `${c.lat.toFixed(6)}, ${c.lng.toFixed(6)}` : "—"}
                  </td>
                  <td className="py-1.5">{c.note || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
